import { PaletteMode } from "@mui/material";
import { ThemeOptions } from "@mui/material/styles";

// ============================================================
// MUI PALETTE EXTENSION - Add custom colors to theme.palette
// ============================================================
declare module "@mui/material/styles" {
  interface TypeText {
    /** Alias of text.primary kept for historical call sites. */
    main: string;
  }
  interface TypeBackground {
    /** Sidebar / rail surface - slightly recessed from `paper`. */
    sidebar: string;
    /** Top app bar surface. */
    header: string;
    /** Subtle fill for table heads, toolbars and inset panels. */
    subtle: string;
  }
  interface Palette {
    surface: {
      hover: string;
      selected: string;
      inset: string;
    };
    slate: {
      hover: string;
      active: string;
      highlight: string;
    };
    infoOverlay: string;
    successOverlay: string;
    warningOverlay: string;
    indigoOverlay: string;
    overlay: {
      success: string;
      successDark: string;
      warning: string;
      warningDark: string;
      error: string;
      errorDark: string;
      info: string;
      infoDark: string;
    };
    borders: {
      light: string;
      dark: string;
      lightSoft: string;
      darkSoft: string;
      lightExtraSoft: string;
      darkExtraSoft: string;
    };
    shadows: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
    statusBorder: {
      success: { dark: string; light: string };
      warning: { dark: string; light: string };
      error: { dark: string; light: string };
      info: { dark: string; light: string };
    };
    fabShadow: {
      primary: string;
      secondary: string;
    };
    errorHover: string;
    actionColors: {
      lightExtraSoft: string;
      contained: string;
      outlined: {
        borderDark: string;
        borderLight: string;
        borderDarkHover: string;
        borderLightHover: string;
        bgDark: string;
        bgLight: string;
      };
    };
    statusBadge: {
      default: {
        dark: { bg: string; border: string };
        light: { bg: string; border: string };
      };
    };
    quickStats: {
      bg: string;
      border: string;
      iconBg: string;
    };
    chip: {
      success: string;
    };
  }
  interface PaletteOptions {
    surface?: {
      hover?: string;
      selected?: string;
      inset?: string;
    };
    slate?: {
      hover?: string;
      active?: string;
      highlight?: string;
    };
    infoOverlay?: string;
    successOverlay?: string;
    warningOverlay?: string;
    indigoOverlay?: string;
    overlay?: {
      success?: string;
      successDark?: string;
      warning?: string;
      warningDark?: string;
      error?: string;
      errorDark?: string;
      info?: string;
      infoDark?: string;
    };
    borders?: {
      light?: string;
      dark?: string;
      lightSoft?: string;
      darkSoft?: string;
      lightExtraSoft?: string;
      darkExtraSoft?: string;
    };
    shadows?: {
      xs?: string;
      sm?: string;
      md?: string;
      lg?: string;
      xl?: string;
    };
    statusBorder?: {
      success?: { dark?: string; light?: string };
      warning?: { dark?: string; light?: string };
      error?: { dark?: string; light?: string };
      info?: { dark?: string; light?: string };
    };
    fabShadow?: {
      primary?: string;
      secondary?: string;
    };
    errorHover?: string;
    actionColors?: {
      lightExtraSoft?: string;
      contained?: string;
      outlined?: {
        borderDark?: string;
        borderLight?: string;
        borderDarkHover?: string;
        borderLightHover?: string;
        bgDark?: string;
        bgLight?: string;
      };
    };
    statusBadge?: {
      default?: {
        dark?: { bg?: string; border?: string };
        light?: { bg?: string; border?: string };
      };
    };
    quickStats?: {
      bg?: string;
      border?: string;
      iconBg?: string;
    };
    chip?: {
      success?: string;
    };
  }
}

// ============================================================
// Design System - Case Hero Admin (Legal Tech SaaS)
//
// Layout language: flat, hairline-bordered surfaces with a
// recessed sidebar, calm data tables and pill-shaped status
// markers. Depth is expressed with 1px borders rather than
// drop shadows; elevation is reserved for overlays.
//
// Brand: slate (authority) + green (confirmation). Selection is
// always a neutral fill - green marks what is active, so colour
// stays meaningful rather than decorative. Dark mode is a neutral
// charcoal with no blue cast.
// ============================================================

const FONT_STACK =
  '"Inter", "Inter var", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const COLORS = {
  // Primary brand colors - Trust & Authority
  primary: {
    dark: "#1E293B",
    main: "#334155",
    light: "#475569",
    contrastText: "#F8FAFC",
  },
  // Secondary - Success confirmation
  secondary: {
    dark: "#15803D",
    main: "#16A34A",
    light: "#4ADE80",
    contrastText: "#FFFFFF",
  },
  // Functional colors
  success: {
    main: "#16A34A",
    light: "#4ADE80",
    dark: "#15803D",
  },
  warning: {
    main: "#D97706",
    light: "#FBBF24",
    dark: "#B45309",
  },
  error: {
    main: "#DC2626",
    light: "#F87171",
    dark: "#B91C1C",
  },
  info: {
    main: "#2563EB",
    light: "#60A5FA",
    dark: "#1D4ED8",
  },
  // ============================================================
  // SURFACES
  // Light: page frame is a hair cooler than the cards sitting on
  // it, so tables read as raised without needing a shadow.
  // Dark: sidebar recedes below the content plane.
  // ============================================================
  background: {
    default: "#F7F8FA",
    paper: "#FFFFFF",
    sidebar: "#FCFCFD",
    header: "#FFFFFF",
    subtle: "#F8FAFC",
  },
  // Dark mode is a neutral charcoal, deliberately free of any blue
  // cast, so the green brand accent is the only colour on the surface.
  backgroundDark: {
    default: "#121212",
    paper: "#1C1C1F",
    sidebar: "#0E0E0F",
    header: "#121212",
    subtle: "#232326",
  },
  // ============================================================
  // TEXT
  // ============================================================
  text: {
    main: "#0F172A",
    secondary: "#64748B",
    disabled: "#94A3B8",
  },
  textDark: {
    main: "#F4F4F5",
    secondary: "#A1A1A6",
    disabled: "#6E6E73",
  },
  // ============================================================
  // INTERACTION SURFACES
  // ============================================================
  surface: {
    hover: {
      dark: "rgba(255,255,255,0.055)",
      light: "rgba(15,23,42,0.035)",
    },
    selected: {
      dark: "rgba(255,255,255,0.10)",
      light: "rgba(15,23,42,0.06)",
    },
    inset: {
      dark: "rgba(255,255,255,0.04)",
      light: "rgba(15,23,42,0.025)",
    },
  },
  // Neutral emphasis for rows and small controls
  slate: {
    hover: {
      dark: "rgba(255,255,255,0.055)",
      light: "rgba(51, 65, 85, 0.05)",
    },
    active: {
      dark: "rgba(255,255,255,0.10)",
      light: "rgba(51, 65, 85, 0.08)",
    },
    // Row highlight for tables
    highlight: {
      dark: "rgba(255,255,255,0.08)",
      light: "rgba(51, 65, 85, 0.07)",
    },
  },
  infoOverlay: {
    dark: "rgba(37, 99, 235, 0.14)",
    light: "rgba(37, 99, 235, 0.07)",
  },
  successOverlay: {
    dark: "rgba(22, 163, 74, 0.14)",
    light: "rgba(22, 163, 74, 0.07)",
  },
  warningOverlay: {
    dark: "rgba(217, 119, 6, 0.14)",
    light: "rgba(217, 119, 6, 0.07)",
  },
  indigoOverlay: {
    dark: "rgba(79, 70, 229, 0.14)",
    light: "rgba(79, 70, 229, 0.07)",
  },
  // Status pill fills
  overlay: {
    success: {
      dark: "rgba(22, 163, 74, 0.16)",
      light: "rgba(22, 163, 74, 0.08)",
    },
    successDark: {
      dark: "rgba(22, 163, 74, 0.16)",
      light: "rgba(22, 163, 74, 0.08)",
    },
    warning: {
      dark: "rgba(217, 119, 6, 0.16)",
      light: "rgba(217, 119, 6, 0.09)",
    },
    warningDark: {
      dark: "rgba(217, 119, 6, 0.16)",
      light: "rgba(217, 119, 6, 0.09)",
    },
    error: {
      dark: "rgba(220, 38, 38, 0.16)",
      light: "rgba(220, 38, 38, 0.08)",
    },
    errorDark: {
      dark: "rgba(220, 38, 38, 0.16)",
      light: "rgba(220, 38, 38, 0.08)",
    },
    info: {
      dark: "rgba(37, 99, 235, 0.16)",
      light: "rgba(37, 99, 235, 0.08)",
    },
    infoDark: {
      dark: "rgba(37, 99, 235, 0.16)",
      light: "rgba(37, 99, 235, 0.08)",
    },
  },
  // Hairline borders - the primary depth cue in this system
  borders: {
    light: "rgba(15,23,42,0.10)",
    dark: "rgba(255,255,255,0.10)",
    lightSoft: "rgba(15,23,42,0.07)",
    darkSoft: "rgba(255,255,255,0.07)",
    lightExtraSoft: "rgba(15,23,42,0.04)",
    darkExtraSoft: "rgba(255,255,255,0.04)",
  },
  // Shadows - reserved for overlays; surfaces use borders instead
  shadows: {
    light: {
      xs: "0 1px 2px rgba(15,23,42,0.04)",
      sm: "0 1px 3px rgba(15,23,42,0.06)",
      md: "0 4px 12px rgba(15,23,42,0.08)",
      lg: "0 12px 28px rgba(15,23,42,0.10)",
      xl: "0 24px 48px rgba(15,23,42,0.14)",
    },
    dark: {
      xs: "0 1px 2px rgba(0,0,0,0.30)",
      sm: "0 1px 3px rgba(0,0,0,0.36)",
      md: "0 4px 12px rgba(0,0,0,0.44)",
      lg: "0 12px 28px rgba(0,0,0,0.52)",
      xl: "0 24px 48px rgba(0,0,0,0.60)",
    },
  },
  // Status pill borders
  statusBorder: {
    success: {
      dark: "rgba(22, 163, 74, 0.35)",
      light: "rgba(22, 163, 74, 0.25)",
    },
    warning: {
      dark: "rgba(217, 119, 6, 0.35)",
      light: "rgba(217, 119, 6, 0.25)",
    },
    error: {
      dark: "rgba(220, 38, 38, 0.35)",
      light: "rgba(220, 38, 38, 0.25)",
    },
    info: {
      dark: "rgba(37, 99, 235, 0.35)",
      light: "rgba(37, 99, 235, 0.25)",
    },
  },
  fabShadow: {
    primary: {
      dark: "rgba(0,0,0,0.5)",
      light: "rgba(51, 65, 85, 0.25)",
    },
    secondary: {
      dark: "rgba(0,0,0,0.5)",
      light: "rgba(22, 163, 74, 0.25)",
    },
  },
  errorHover: {
    dark: "rgba(220, 38, 38, 0.16)",
    light: "rgba(220, 38, 38, 0.08)",
  },
  action: {
    lightExtraSoft: {
      dark: "rgba(255,255,255,0.05)",
      light: "rgba(15,23,42,0.04)",
    },
    contained: {
      dark: "rgba(255,255,255,0.16)",
      light: "rgba(51, 65, 85, 0.14)",
    },
    outlined: {
      borderDark: "rgba(255,255,255,0.16)",
      borderLight: "rgba(15,23,42,0.12)",
      borderDarkHover: "rgba(255,255,255,0.30)",
      borderLightHover: "rgba(15,23,42,0.24)",
      bgDark: "rgba(255,255,255,0.05)",
      bgLight: "rgba(15,23,42,0.03)",
    },
  },
  statusBadge: {
    default: {
      dark: {
        bg: "rgba(255,255,255,0.06)",
        border: "rgba(255,255,255,0.12)",
      },
      light: {
        bg: "rgba(15,23,42,0.04)",
        border: "rgba(15,23,42,0.10)",
      },
    },
  },
  quickStats: {
    bg: {
      dark: "#1C1C1F",
      light: "#FFFFFF",
    },
    border: {
      dark: "rgba(255,255,255,0.08)",
      light: "rgba(15,23,42,0.08)",
    },
    iconBg: {
      dark: "rgba(255,255,255,0.08)",
      light: "rgba(51, 65, 85, 0.07)",
    },
  },
  chip: {
    success: {
      dark: "rgba(22, 163, 74, 0.16)",
      light: "rgba(22, 163, 74, 0.09)",
    },
  },
} as const;

// ============================================================
// TYPOGRAPHY
// Colour is intentionally absent - variants inherit from
// palette.text so a single mode switch recolours everything.
// ============================================================
const TYPOGRAPHY = {
  fontFamily: FONT_STACK,
  h1: {
    fontWeight: 700,
    fontSize: "1.875rem",
    lineHeight: 1.25,
    letterSpacing: "-0.02em",
  },
  h2: {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.3,
    letterSpacing: "-0.018em",
  },
  h3: {
    fontWeight: 600,
    fontSize: "1.25rem",
    lineHeight: 1.35,
    letterSpacing: "-0.014em",
  },
  h4: {
    fontWeight: 600,
    fontSize: "1.0625rem",
    lineHeight: 1.4,
    letterSpacing: "-0.01em",
  },
  h5: {
    fontWeight: 600,
    fontSize: "0.9375rem",
    lineHeight: 1.45,
    letterSpacing: "-0.006em",
  },
  h6: {
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    letterSpacing: "0",
  },
  subtitle1: {
    fontWeight: 500,
    fontSize: "0.9375rem",
    lineHeight: 1.5,
  },
  subtitle2: {
    fontWeight: 500,
    fontSize: "0.8125rem",
    lineHeight: 1.55,
  },
  body1: {
    fontWeight: 400,
    fontSize: "0.9375rem",
    lineHeight: 1.6,
  },
  body2: {
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: 1.55,
  },
  caption: {
    fontWeight: 400,
    fontSize: "0.75rem",
    lineHeight: 1.5,
  },
  overline: {
    fontWeight: 600,
    fontSize: "0.6875rem",
    lineHeight: 1.6,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
  },
  button: {
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.6,
    letterSpacing: "0",
    textTransform: "none" as const,
  },
};

// ============================================================
// THEME CREATION
// ============================================================

export const createCaseHeroTheme = (mode: PaletteMode): ThemeOptions => {
  const isDark = mode === "dark";
  const pick = <T,>(values: { dark: T; light: T }): T =>
    isDark ? values.dark : values.light;

  const surfaces = isDark ? COLORS.backgroundDark : COLORS.background;
  const textColors = isDark ? COLORS.textDark : COLORS.text;
  const shadowScale = isDark ? COLORS.shadows.dark : COLORS.shadows.light;
  const hairline = isDark ? COLORS.borders.dark : COLORS.borders.light;
  const hairlineSoft = isDark
    ? COLORS.borders.darkSoft
    : COLORS.borders.lightSoft;

  return {
    palette: {
      mode,
      primary: {
        main: isDark ? "#E4E4E7" : COLORS.primary.main,
        dark: isDark ? "#A1A1AA" : COLORS.primary.dark,
        light: isDark ? "#FAFAFA" : COLORS.primary.light,
        contrastText: isDark ? "#18181B" : COLORS.primary.contrastText,
      },
      secondary: {
        main: COLORS.secondary.main,
        dark: COLORS.secondary.dark,
        light: COLORS.secondary.light,
        contrastText: COLORS.secondary.contrastText,
      },
      success: COLORS.success,
      warning: COLORS.warning,
      error: COLORS.error,
      info: COLORS.info,
      background: {
        default: surfaces.default,
        paper: surfaces.paper,
        sidebar: surfaces.sidebar,
        header: surfaces.header,
        subtle: surfaces.subtle,
      },
      text: {
        primary: textColors.main,
        main: textColors.main,
        secondary: textColors.secondary,
        disabled: textColors.disabled,
      },
      divider: hairline,
      action: {
        hover: pick(COLORS.surface.hover),
        selected: pick(COLORS.surface.selected),
        disabledBackground: pick(COLORS.surface.selected),
        hoverOpacity: isDark ? 0.08 : 0.04,
        selectedOpacity: isDark ? 0.12 : 0.08,
      },
      surface: {
        hover: pick(COLORS.surface.hover),
        selected: pick(COLORS.surface.selected),
        inset: pick(COLORS.surface.inset),
      },
      slate: {
        hover: pick(COLORS.slate.hover),
        active: pick(COLORS.slate.active),
        highlight: pick(COLORS.slate.highlight),
      },
      infoOverlay: pick(COLORS.infoOverlay),
      successOverlay: pick(COLORS.successOverlay),
      warningOverlay: pick(COLORS.warningOverlay),
      indigoOverlay: pick(COLORS.indigoOverlay),
      overlay: {
        success: pick(COLORS.overlay.success),
        successDark: pick(COLORS.overlay.successDark),
        warning: pick(COLORS.overlay.warning),
        warningDark: pick(COLORS.overlay.warningDark),
        error: pick(COLORS.overlay.error),
        errorDark: pick(COLORS.overlay.errorDark),
        info: pick(COLORS.overlay.info),
        infoDark: pick(COLORS.overlay.infoDark),
      },
      borders: { ...COLORS.borders },
      shadows: { ...shadowScale },
      statusBorder: {
        success: { ...COLORS.statusBorder.success },
        warning: { ...COLORS.statusBorder.warning },
        error: { ...COLORS.statusBorder.error },
        info: { ...COLORS.statusBorder.info },
      },
      fabShadow: {
        primary: pick(COLORS.fabShadow.primary),
        secondary: pick(COLORS.fabShadow.secondary),
      },
      errorHover: pick(COLORS.errorHover),
      actionColors: {
        lightExtraSoft: pick(COLORS.action.lightExtraSoft),
        contained: pick(COLORS.action.contained),
        outlined: { ...COLORS.action.outlined },
      },
      statusBadge: {
        default: {
          dark: { ...COLORS.statusBadge.default.dark },
          light: { ...COLORS.statusBadge.default.light },
        },
      },
      quickStats: {
        bg: pick(COLORS.quickStats.bg),
        border: pick(COLORS.quickStats.border),
        iconBg: pick(COLORS.quickStats.iconBg),
      },
      chip: {
        success: pick(COLORS.chip.success),
      },
    },
    typography: TYPOGRAPHY,
    shape: {
      borderRadius: 10,
    },
    // Flat by design: only the top few levels are used, and only
    // by overlays (menus, dialogs, popovers).
    shadows: [
      "none",
      shadowScale.xs,
      shadowScale.sm,
      shadowScale.sm,
      shadowScale.md,
      shadowScale.md,
      shadowScale.lg,
      shadowScale.lg,
      shadowScale.xl,
      ...Array(16).fill(shadowScale.xl),
    ] as any,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*, *::before, *::after": {
            boxSizing: "border-box",
          },
          body: {
            fontFamily: FONT_STACK,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            backgroundColor: surfaces.default,
          },
          // Slim scrollbars so the sidebar and tables stay quiet
          "*::-webkit-scrollbar": {
            width: 10,
            height: 10,
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.16)"
              : "rgba(15,23,42,0.16)",
            borderRadius: 8,
            border: "3px solid transparent",
            backgroundClip: "content-box",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.24)"
              : "rgba(15,23,42,0.28)",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            borderRadius: 8,
            padding: "7px 14px",
            minHeight: 36,
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": {
              boxShadow: "none",
            },
          },
          outlined: {
            borderColor: hairline,
            color: textColors.main,
            "&:hover": {
              borderColor: isDark
                ? COLORS.action.outlined.borderDarkHover
                : COLORS.action.outlined.borderLightHover,
              backgroundColor: pick(COLORS.surface.hover),
            },
          },
          text: {
            padding: "7px 10px",
          },
          sizeSmall: {
            minHeight: 32,
            padding: "4px 10px",
            fontSize: "0.8125rem",
          },
          sizeLarge: {
            minHeight: 42,
            padding: "10px 20px",
            fontSize: "0.9375rem",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            color: textColors.secondary,
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
              backgroundColor: pick(COLORS.surface.hover),
              color: textColors.main,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          outlined: {
            borderColor: hairline,
          },
          elevation1: {
            boxShadow: shadowScale.xs,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            boxShadow: "none",
            borderRadius: 12,
            border: `1px solid ${hairline}`,
            "& .MuiCardContent-root:last-child": {
              paddingBottom: 20,
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: "16px 20px",
            borderBottom: `1px solid ${hairlineSoft}`,
          },
          title: {
            fontSize: "0.9375rem",
            fontWeight: 600,
          },
          subheader: {
            fontSize: "0.8125rem",
            color: textColors.secondary,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 20,
          },
        },
      },
      // Case views are built from stacked accordions; style them once
      // here so every section reads as the same bordered panel.
      MuiAccordion: {
        defaultProps: {
          disableGutters: true,
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${hairline}`,
            borderRadius: 12,
            backgroundColor: surfaces.paper,
            backgroundImage: "none",
            overflow: "hidden",
            "&::before": { display: "none" },
            "&.Mui-expanded": { margin: 0 },
            "& + &": { marginTop: 16 },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 52,
            padding: "0 16px",
            "&.Mui-expanded": {
              minHeight: 52,
              borderBottom: `1px solid ${hairline}`,
            },
          },
          content: {
            margin: "12px 0",
            fontSize: "0.9375rem",
            fontWeight: 600,
            "&.Mui-expanded": { margin: "12px 0" },
            // Section titles are often wrapped in <Typography>; make it
            // inherit so every accordion heading matches.
            "& > .MuiTypography-root": {
              fontSize: "inherit",
              fontWeight: "inherit",
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: 16,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: surfaces.paper,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: surfaces.subtle,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background-color 0.15s ease",
            "&:last-child td": {
              borderBottom: 0,
            },
          },
          hover: {
            "&:hover": {
              backgroundColor: pick(COLORS.surface.hover),
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "14px 16px",
            fontSize: "0.875rem",
            borderBottom: `1px solid ${hairlineSoft}`,
          },
          // Reference-style head: sentence case, muted, no shouting
          head: {
            fontWeight: 500,
            fontSize: "0.8125rem",
            textTransform: "none",
            letterSpacing: "0",
            color: textColors.secondary,
            backgroundColor: surfaces.subtle,
            padding: "12px 16px",
            whiteSpace: "nowrap",
            borderBottom: `1px solid ${hairline}`,
          },
          stickyHeader: {
            backgroundColor: surfaces.subtle,
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${hairlineSoft}`,
            fontSize: "0.8125rem",
          },
          selectLabel: { fontSize: "0.8125rem" },
          displayedRows: { fontSize: "0.8125rem" },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: surfaces.paper,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: hairline,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark
                ? COLORS.action.outlined.borderDarkHover
                : COLORS.action.outlined.borderLightHover,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#E4E4E7" : COLORS.primary.main,
              borderWidth: 1.5,
            },
          },
          input: {
            padding: "10px 12px",
            fontSize: "0.875rem",
          },
          inputSizeSmall: {
            padding: "7px 12px",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            color: textColors.secondary,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: "10px 12px",
            fontSize: "0.875rem",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${hairline}`,
            boxShadow: shadowScale.lg,
            marginTop: 4,
          },
          list: {
            padding: 6,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontSize: "0.875rem",
            minHeight: 36,
            "&:hover": {
              backgroundColor: pick(COLORS.surface.hover),
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${hairline}`,
            backgroundImage: "none",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            fontWeight: 600,
            padding: "18px 20px",
            borderBottom: `1px solid ${hairlineSoft}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: "0.75rem",
            padding: "6px 10px",
            backgroundColor: isDark ? "#2A2A2E" : "#18181B",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: "0.75rem",
            borderRadius: 6,
            height: 24,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            borderRadius: 10,
            fontWeight: 600,
            fontSize: "0.6875rem",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${hairline}`,
            backgroundColor: surfaces.sidebar,
            backgroundImage: "none",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            backgroundImage: "none",
            borderBottom: `1px solid ${hairline}`,
            backgroundColor: surfaces.header,
            color: textColors.main,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 34,
            color: textColors.secondary,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 500,
            fontSize: "0.875rem",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 42,
            borderBottom: `1px solid ${hairline}`,
          },
          indicator: {
            height: 2,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            minHeight: 42,
            padding: "10px 14px",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: hairlineSoft,
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: "0.8125rem",
            fontWeight: 500,
            minWidth: 34,
            height: 34,
            border: `1px solid ${hairline}`,
            "&.Mui-selected": {
              backgroundColor: isDark ? "#E4E4E7" : COLORS.primary.main,
              color: isDark ? "#18181B" : COLORS.primary.contrastText,
              borderColor: "transparent",
              "&:hover": {
                backgroundColor: isDark ? "#FAFAFA" : COLORS.primary.dark,
              },
            },
          },
          ellipsis: {
            border: "none",
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: shadowScale.md,
            "&:hover": {
              boxShadow: shadowScale.lg,
            },
          },
        },
      },
    },
  };
};

// ============================================================
// TYPE EXPORTS
// ============================================================

export type CaseHeroTheme = ReturnType<typeof createCaseHeroTheme>;
